import { useQuery } from '@apollo/client';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { GetAvailableRolesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalForm } from 'src/components/form/form/ModalForm';

import {
  useUpdateCustomRoles,
  useUpdateUserRoles,
} from '@/hooks/mutations/user';
import {
  useGetAuthUserByIdWithRoles,
  useGetCustomRoles,
} from '@/hooks/queries';
import { useIsCustomerSupport } from '@/hooks/useIsCustomerSupport';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import UserDetailsForm from './forms/UserDetailsForm';
import type {
  UserDetailsFormFields,
  UserDetailsFormFieldsMultiRole,
} from './forms/UserDetailsSchema';
import {
  UserDetailsSchema,
  UserDetailsSchemaMultiRole,
} from './forms/UserDetailsSchema';

type Props = {
  onDismiss: () => void;
  id: string;
};

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isUUID = (value: string) => UUID_REGEX.test(value);

const UserDetailsModal: FC<Props> = ({ onDismiss, id }) => {
  const { addNotification } = useNotifications();
  const { t } = useTranslation();
  const { updateUserRoles } = useUpdateUserRoles();
  const { updateCustomRoles } = useUpdateCustomRoles();

  // Check if the new granular role system (trpc feature) is enabled
  const isMultiRoleEnabled = useIsFeatureFlagEnabled('trpc');

  const callerIsCS = useIsCustomerSupport();

  const { data: getUserResponse, loading: loadingUser } =
    useGetAuthUserByIdWithRoles({
      queryArgs: { id },
      shouldSkip: !id,
    });
  const user = getUserResponse?.auth_user_by_pk;
  const userOrg = user?.organisationusers[0];

  // Show system roles (e.g. CustomerSuccess) only when:
  // - multi-role mode is enabled
  // - the logged-in user is a CS user
  // - the target user is a RiskSmart internal user (IsCustomerSupport)
  const showSystemRoles =
    isMultiRoleEnabled && callerIsCS && !!user?.IsCustomerSupport;

  // Prepare form values based on whether multi-role mode is enabled
  const userValues: UserDetailsFormFields | UserDetailsFormFieldsMultiRole =
    isMultiRoleEnabled
      ? {
          UserId: user?.Id ?? '',
          Roles: [
            // Existing custom roles
            ...(user?.customRoles?.map((cr) => ({
              label: cr.role.RoleName,
              value: cr.role.Id,
              description: cr.role.Description ?? '',
            })) ?? []),
          ],
        }
      : {
          UserId: user?.Id ?? '',
          Role: userOrg?.RoleKey ?? user?.RoleKey ?? '',
        };

  const { data: getRolesResponse, loading: loadingRoles } = useQuery(
    GetAvailableRolesDocument,
    {
      onError: (error) => {
        addNotification({
          type: 'error',
          content: <>{error.message}</>,
        });
      },
      // Fetch system roles when: single-role mode OR (multi-role mode + CS caller + RiskSmart target)
      skip: isMultiRoleEnabled && !showSystemRoles,
      fetchPolicy: 'no-cache',
    }
  );
  const { data: getCustomRolesData, loading: loadingCustomRoles } =
    useGetCustomRoles({
      queryArgs: {},
      shouldSkip: !isMultiRoleEnabled,
    });
  const availableRoles = getRolesResponse?.available_roles ?? [];

  // System role options from GetAvailableRoles (CustomerSuccess etc.)
  const systemRoleOptions = showSystemRoles
    ? availableRoles.map((role) => ({
        id: role.id,
        value: role.name,
        description: role.description,
      }))
    : [];

  // When showing system roles, also pre-populate existing system role selections
  if (isMultiRoleEnabled && showSystemRoles && 'Roles' in userValues) {
    const existingSystemRoles = (user?.userRoles ?? [])
      .filter((ur) => systemRoleOptions.some((opt) => opt.id === ur.RoleKey))
      .map((ur) => {
        const opt = systemRoleOptions.find((o) => o.id === ur.RoleKey);

        return {
          label: opt?.value ?? ur.RoleKey,
          value: ur.RoleKey,
          description: opt?.description ?? '',
        };
      });
    userValues.Roles = [...userValues.Roles, ...existingSystemRoles];
  }

  const roleOptions = !isMultiRoleEnabled
    ? availableRoles
        .filter(
          (role) =>
            role.name !== 'CustomerSupport' ||
            (callerIsCS && !!user?.IsCustomerSupport)
        )
        .map((role) => ({
          id: role.id,
          value: role.name,
          description: role.description,
        }))
        .sort((a, b) => a.value.localeCompare(b.value))
    : [
        // Custom roles from org-specific table
        ...(getCustomRolesData?.custom_role ?? []).map((role) => ({
          id: role.Id,
          value: role.RoleName,
          description: role.Description ?? undefined,
        })),
        // System roles (e.g. CustomerSuccess) — only for CS callers editing RiskSmart users
        ...systemRoleOptions,
      ].sort((a, b) => a.value.localeCompare(b.value));

  const onSave = async (
    values: UserDetailsFormFields | UserDetailsFormFieldsMultiRole
  ) => {
    if (isMultiRoleEnabled && 'Roles' in values) {
      const { UserId, Roles } = values;

      // Split selected roles into custom roles (UUID) and system roles (RoleKey)
      const customRoleIds = Roles.filter((r) => isUUID(r.value)).map(
        (r) => r.value
      );
      const selectedSystemRoleKeys = Roles.filter((r) => !isUUID(r.value)).map(
        (r) => r.value
      );

      // Always update custom roles
      await updateCustomRoles({
        userId: UserId,
        customRoleIds,
      });

      // Update system roles when the section is visible
      if (showSystemRoles) {
        // Preserve any existing system roles not shown in the picker
        const nonVisibleSystemRoleKeys = (user?.userRoles ?? [])
          .map((ur) => ur.RoleKey)
          .filter((key) => !systemRoleOptions.some((opt) => opt.id === key));

        const allSystemRoleKeys = [
          ...selectedSystemRoleKeys,
          ...nonVisibleSystemRoleKeys,
        ];

        await updateUserRoles({
          userId: UserId,
          roleIds: allSystemRoleKeys,
        });
      }
    } else if (!isMultiRoleEnabled && 'Role' in values) {
      // Single role mode: convert to array for backward compatibility
      const { UserId, Role } = values;
      const roleIds = availableRoles
        .filter((role) => role.name === Role)
        .map((role) => role.id);

      await updateUserRoles({
        userId: UserId,
        roleIds,
      });
    }
  };

  if (loadingUser || loadingRoles || loadingCustomRoles) {
    return null;
  }

  const formId = 'user-form';
  const isReadOnly = false;

  return (
    <ModalForm
      i18n={t('userSettings')}
      values={userValues}
      defaultValues={isMultiRoleEnabled ? { Roles: [] } : { Role: '' }}
      schema={
        isMultiRoleEnabled ? UserDetailsSchemaMultiRole : UserDetailsSchema
      }
      onSave={onSave}
      onDismiss={onDismiss}
      formId={formId}
      visible={true}
      readOnly={isReadOnly}
    >
      <UserDetailsForm
        roleOptions={roleOptions}
        readOnly={isReadOnly}
        multiRoleMode={isMultiRoleEnabled}
      />
    </ModalForm>
  );
};

export default UserDetailsModal;
